// UserMenu — account pill + dropdown (identity, appearance/theme + language
// toggles, switch company, profile, logout). Identity (name, role, company)
// comes from the auth slice, populated by the select-company response — the
// authToken is never decoded. Language switch drives i18next + the language
// slice; theme drives the theme slice; logout clears auth and returns to /login.
//
// Switch company opens SwitchCompanyModal in place rather than navigating to
// /select-company — that route stays for the post-login flow, but sending a
// signed-in user back out to the auth shell to change tenant was never the
// design.

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Dropdown, Icon } from '@/components/common';
import SwitchCompanyModal from './SwitchCompanyModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { switchLanguage, AppLanguage } from '@/redux/language/languageSlice';
import { toggleTheme } from '@/redux/theme/themeSlice';
import { clearAuth } from '@/redux/auth/authSlice';
import { selectAuthUser, selectIsOwner, selectCompanyDetail } from '@/redux/auth/selectors';

/** Avatar + name + a secondary line, shared by the trigger pill and the header. */
const IdentityBlock = ({
  initials,
  name,
  secondary,
  avatarSize = 34,
}: {
  initials: string;
  name: string;
  secondary: ReactNode;
  avatarSize?: number;
}) => (
  <>
    <Avatar initials={initials} size={avatarSize} />
    <span className='app-user-menu__text'>
      <span className='app-user-menu__name'>{name}</span>
      <span className='app-user-menu__role'>{secondary}</span>
    </span>
  </>
);

const UserMenu = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => state.theme.mode);
  const user = useAppSelector(selectAuthUser);
  const isOwner = useAppSelector(selectIsOwner);
  const companyDetail = useAppSelector(selectCompanyDetail);
  const [open, setOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);

  const lang = (i18n.language === 'ar' ? 'ar' : 'en') as AppLanguage;

  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const roleText = t(isOwner ? 'select_owner' : 'select_admin');
  const companyName = companyDetail?.companyName;

  // Company · Role — tokens rendered as separate bidi-isolated spans so a Latin
  // company name next to an Arabic role doesn't reorder around the separator.
  const identityMeta = (
    <span className='app-user-menu__meta'>
      {companyName && (
        <>
          <span className='app-user-menu__meta-item'>{companyName}</span>
          <span className='app-user-menu__meta-sep' aria-hidden='true'>
            ·
          </span>
        </>
      )}
      <span className='app-user-menu__meta-item'>{roleText}</span>
    </span>
  );

  const toggleLanguage = () => {
    const next: AppLanguage = lang === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('lang', next);
    dispatch(switchLanguage(next));
  };

  const goSwitchCompany = () => {
    setOpen(false);
    setSwitchOpen(true);
  };

  const goProfile = () => {
    setOpen(false);
    navigate('/my-profile');
  };

  const logout = () => {
    setOpen(false);
    dispatch(clearAuth());
    navigate('/login');
  };

  return (
  <>
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      width={284}
      trigger={
        <button
          type='button'
          className={`app-user-menu${open ? ' is-open' : ''}`}
          aria-haspopup='menu'
          aria-expanded={open}
          aria-label={t('header_user_menu')}
          onClick={() => setOpen((o) => !o)}
        >
          <IdentityBlock initials={initials} name={name} secondary={roleText} />
          <span className='app-user-menu__chevron' aria-hidden='true' />
        </button>
      }
    >
      <div className='app-dropdown__header'>
        <IdentityBlock initials={initials} name={name} secondary={identityMeta} avatarSize={40} />
      </div>

      <div className='app-dropdown__divider' role='separator' />

      <button
        type='button'
        className='app-dropdown__item'
        role='menuitemcheckbox'
        aria-checked={mode === 'dark'}
        onClick={() => dispatch(toggleTheme())}
      >
        <Icon name='sun-moon' size={16} />
        <span className='app-dropdown__item-label'>{t('header_appearance')}</span>
        <span className='app-menu-pill' aria-hidden='true'>
          <span className={`app-menu-pill__label${mode === 'light' ? ' is-active' : ''}`}>
            <Icon name='sun' size={12} />
          </span>
          <span className={`app-menu-pill__pill${mode === 'dark' ? ' is-end' : ''}`}>
            <span className='app-menu-pill__dot' />
          </span>
          <span className={`app-menu-pill__label${mode === 'dark' ? ' is-active' : ''}`}>
            <Icon name='moon' size={12} />
          </span>
        </span>
      </button>

      <button
        type='button'
        className='app-dropdown__item'
        role='menuitemcheckbox'
        aria-checked={lang === 'ar'}
        aria-label={t('select_language')}
        onClick={toggleLanguage}
      >
        <Icon name='languages' size={16} />
        <span className='app-dropdown__item-label'>{t('select_language')}</span>
        <span className='app-menu-pill' aria-hidden='true'>
          <span className={`app-menu-pill__label${lang === 'en' ? ' is-active' : ''}`}>EN</span>
          <span className={`app-menu-pill__pill${lang === 'ar' ? ' is-end' : ''}`}>
            <span className='app-menu-pill__dot' />
          </span>
          <span className={`app-menu-pill__label${lang === 'ar' ? ' is-active' : ''}`}>AR</span>
        </span>
      </button>

      <div className='app-dropdown__divider' role='separator' />

      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={goSwitchCompany}
      >
        <Icon name='building-2' size={16} />
        <span className='app-dropdown__item-label'>{t('header_switch_company')}</span>
      </button>
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={goProfile}
      >
        <Icon name='user' size={16} />
        <span className='app-dropdown__item-label'>{t('header_my_profile')}</span>
      </button>

      <div className='app-dropdown__divider' role='separator' />

      <button
        type='button'
        className='app-dropdown__item app-dropdown__item--danger'
        role='menuitem'
        onClick={logout}
      >
        <Icon name='log-out' size={16} />
        <span className='app-dropdown__item-label'>{t('select_logout')}</span>
      </button>
    </Dropdown>

    <SwitchCompanyModal open={switchOpen} onClose={() => setSwitchOpen(false)} />
  </>
  );
};

export default UserMenu;
